import _ from 'lodash';

import {
  AutoAwesomeOutlined,
  InfoOutlined
} from '@mui/icons-material';
import {
  Box,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import Big from 'big.js';
import {
  CalculationInput,
  CalculationMetric,
  CalculationSentiment
} from 'generated/graphql';
import { popperUtil } from 'modules/common';
import { CalculationRatingWidget } from 'modules/components/layer01';
import {
  CalculationInputPopper,
  CalculationRulePopper,
  CalculationSentimentSummaryPopper
} from 'modules/components/layer17';
import { useState } from 'react';



export type CalculationDataObject = {
  id: string;
  isEnabled: boolean;
  formula: string;
  metric: CalculationMetric;
  inputs: CalculationInput[];
  output?: Big;
  unit: string;
  formattedValue: string;
  sentiment: CalculationSentiment;
  summary: string;
  calculationDescription: string;
  nmfSentimentDescription: string;
  neutralSentimentDescription: string;
  badSentimentDescription: string;
  goodSentimentDescription: string;
};

export type CalculationMetricTableRowProps = {
  calculation: CalculationDataObject;
  overlayText?: string;
};

export const FC: React.FC<CalculationMetricTableRowProps> = ({
  calculation, overlayText
}: CalculationMetricTableRowProps) => {
  const rulesPopper = popperUtil.useBasePopperState();
  const inputPopper = popperUtil.useBasePopperState();
  const sentimentSummaryPopper = popperUtil.useBasePopperState();
  const rules = _.compact([
    calculation.nmfSentimentDescription,
    calculation.neutralSentimentDescription,
    calculation.badSentimentDescription,
    calculation.goodSentimentDescription
  ]);
  const [
    hovered,
    setHovered
  ] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          ':hover': {
            backgroundColor: '#F4F4F4'
          },
          position: 'relative'
        }}
        onMouseOver={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
        }}
      >
        <TableCell>
          {calculation.metric.displayName}

          {overlayText && (
            <Box sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(2px)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '4px'
            }}
            >
              <Typography
                variant='formLabel'
                sx={{
                  fontWeight: 'bold'
                }}
              >
                Analyzing...
              </Typography>
            </Box>
          )}
        </TableCell>

        <TableCell
          onMouseOver={(r) => {
            if (!inputPopper.open) {
              inputPopper.handleClick(r);
            }
          }}
          onMouseLeave={() => inputPopper.handleClose()}
        >
          {calculation.inputs.length > 0 && (
            <Stack
              direction='row'
              gap={1}
              sx={{
                justifyContent: 'flex-end',
                cursor: 'pointer'
              }}
            >
              <Box>
                <InfoOutlined
                  fontSize='small'
                  sx={{
                    opacity: hovered ? 1 : 0
                  }}
                />
              </Box>

              {calculation.inputs.length}
            </Stack>
          )}
        </TableCell>

        <TableCell>
          {calculation.formattedValue}
        </TableCell>

        <TableCell
          onMouseOver={(r) => {
            if (calculation.summary) {
              sentimentSummaryPopper.handleClick(r);
            }
          }}
          onMouseLeave={() => {
            sentimentSummaryPopper.handleClose();
          }}
        >
          <Box sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            cursor: calculation.summary ? 'pointer' : 'unset'
          }}
          >
            <Box sx={{
              flex: 1
            }}
            />
            <CalculationRatingWidget.FC
              sentiment={calculation.sentiment}
            />

            {calculation.summary && (
              <Box sx={{
              }}
              >
                <AutoAwesomeOutlined
                  sx={{
                    '@media print': {
                      display: 'none'
                    }
                  }}
                  fontSize='small'
                  color='primary'
                />
              </Box>
            )}
          </Box>
        </TableCell>

        <TableCell
          sx={{
            cursor: 'pointer'
          }}
          onMouseEnter={(r) => {
            rulesPopper.handleClick(r);
            rulesPopper.anchorEl;
          }}
          onMouseLeave={() => rulesPopper.handleClose()}
        >
          <Stack
            direction='row'
            sx={{
              gap: 1,
              justifyContent: 'flex-end'
            }}
          >
            <InfoOutlined
              fontSize='small'
              sx={{
                opacity: hovered ? 1 : 0
              }}
            />
            {rules.length || '-'}
          </Stack>
        </TableCell>
      </TableRow>



      {hovered && (
        <CalculationInputPopper.FC
          {...inputPopper}
          formula={calculation.calculationDescription}
          inputs={calculation.inputs}
          id={calculation.id}
        />
      )}

      {rulesPopper.open && (
        <CalculationRulePopper.FC
          {...rulesPopper}
          id={`${calculation.id}-rules`}
          bad={calculation.badSentimentDescription}
          good={calculation.goodSentimentDescription}
          nmf={calculation.nmfSentimentDescription}
          neutral={calculation.neutralSentimentDescription}
        />
      )}

      {sentimentSummaryPopper.open && (
        <CalculationSentimentSummaryPopper.FC
          {...sentimentSummaryPopper}
          id={`${calculation.id}-summary`}
          summary={calculation.summary}
        />
      )}
    </>
  );
};
